
import { useEffect, useRef, useState } from 'react';
import { api, type EnrichmentRecord, type EnrichmentTipo } from '../api';

export type PregenStatus = 'idle' | 'streaming' | 'ready' | 'error';
export type PregenDim = 'recuperacion' | 'comprension' | 'analisis' | 'transferencia';

export interface PregenInputs {
  siteId: string;
  oa_codigo: string;
  oa_descripcion: string;
  asignatura: string;
  nivel_academico: string;
  hito_nombre: string;
  hito_descripcion: string;
  tipo: EnrichmentTipo;
}

export interface PregenState {
  status: PregenStatus;
  /** Dims listas en orden de llegada — orden interno del LLM, no determinista. */
  dimsReady: PregenDim[];
  enrichment: EnrichmentRecord | null;
  latencyMs: number | null;
  error: string | null;
}

const INITIAL: PregenState = {
  status: 'idle',
  dimsReady: [],
  enrichment: null,
  latencyMs: null,
  error: null,
};


export function useMatrixPregeneration(inputs: PregenInputs | null): PregenState {
  const [state, setState] = useState<PregenState>(INITIAL);
  const abortRef = useRef<AbortController | null>(null);
  const inputKey = inputs
    ? `${inputs.siteId}|${inputs.oa_codigo}|${inputs.asignatura}|${inputs.nivel_academico}|${inputs.hito_nombre}|${inputs.tipo}`
    : null;

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();

    if (!inputs || !inputKey) {
      setState(INITIAL);
      return;
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const debounceTimer = setTimeout(() => {
      if (ctrl.signal.aborted) return;
      setState({ ...INITIAL, status: 'streaming' });

      api
        .streamEnrichment(
          inputs.siteId,
          {
            oa_codigo: inputs.oa_codigo,
            oa_descripcion: inputs.oa_descripcion,
            asignatura: inputs.asignatura,
            nivel_academico: inputs.nivel_academico,
            hito_nombre: inputs.hito_nombre,
            hito_descripcion: inputs.hito_descripcion,
            tipo: inputs.tipo,
          },
          {
            onDim: (d) => {
              if (ctrl.signal.aborted) return;
              setState((s) => ({
                ...s,
                dimsReady: s.dimsReady.includes(d.dim as PregenDim)
                  ? s.dimsReady
                  : [...s.dimsReady, d.dim as PregenDim],
              }));
            },
            onComplete: (d) => {
              if (ctrl.signal.aborted) return;
              setState({
                status: 'ready',
                dimsReady: ['recuperacion', 'comprension', 'analisis', 'transferencia'],
                enrichment: d.enrichment,
                latencyMs: d.latency_ms_total,
                error: null,
              });
            },
            onError: (msg) => {
              if (ctrl.signal.aborted) return;
              setState({ ...INITIAL, status: 'error', error: msg });
            },
          },
          ctrl.signal,
        )
        .catch((err) => {
          if (ctrl.signal.aborted) return;
          const msg = err instanceof Error ? err.message : String(err);
          if (msg.toLowerCase().includes('abort')) return;
          setState({ ...INITIAL, status: 'error', error: msg });
        });
    }, 400);

    return () => {
      clearTimeout(debounceTimer);
      ctrl.abort();
    };
  }, [inputKey]);

  return state;
}
